// The warehouse app: full screen, built for a phone or rugged handheld. Home, lookup, bin moves, picks, replenishment,
// count sheets and quick counts.
import { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { wms, type Found, type Movements, type RecentMove, type ScanResult } from "./wmsApi";
import { Header, I, LocChip, ScanField, Alert, feedback, fmt, errText, errBody, getLocation, setLocation } from "./ui";
import { MovePage } from "./Move";
import { PickListPage, PickPage } from "./Pick";
import { ReplenListPage, ReplenTaskPage } from "./Replenish";
import { CountListPage, CountSheetPage } from "./Count";
import { QuickCountPage } from "./Quick";
import { wmsCounts } from "../counts/countsApi";
import "./wms.css";

export function WmsApp({ name }: { name: string }) {
  const [loc, setLoc] = useState(getLocation());
  const choose = (c: string) => { setLocation(c); setLoc(c); };
  return (
    <div className="wms">
      <Routes>
        <Route index element={<Home name={name} loc={loc} choose={choose} />} />
        <Route path="lookup" element={loc ? <Lookup loc={loc} /> : <Home name={name} loc={loc} choose={choose} />} />
        <Route path="move" element={loc ? <MovePage loc={loc} /> : <Home name={name} loc={loc} choose={choose} />} />
        <Route path="picks" element={loc ? <PickListPage loc={loc} /> : <Home name={name} loc={loc} choose={choose} />} />
        <Route path="picks/:type/:no" element={<PickPage />} />
        <Route path="replen" element={loc ? <ReplenListPage loc={loc} /> : <Home name={name} loc={loc} choose={choose} />} />
        <Route path="replen/:id" element={<ReplenTaskPage />} />
        <Route path="count" element={loc ? <CountListPage loc={loc} /> : <Home name={name} loc={loc} choose={choose} />} />
        <Route path="count/:id" element={<CountSheetPage />} />
        <Route path="quick" element={loc ? <QuickCountPage loc={loc} /> : <Home name={name} loc={loc} choose={choose} />} />
        <Route path="*" element={<Home name={name} loc={loc} choose={choose} />} />
      </Routes>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function Home({ name, loc, choose }: { name: string; loc: string; choose: (c: string) => void }) {
  const nav = useNavigate();
  const [locations, setLocations] = useState<{ code: string; name: string }[] | null>(null);
  const [picking, setPicking] = useState(!loc);
  const [counts, setCounts] = useState<{ mine: number; unassigned: number } | null>(null);
  const [repl, setRepl] = useState<{ total: number; urgent: number; rules: number; feed: boolean } | null>(null);
  const [sheets, setSheets] = useState<{ mine: number; open: number } | null>(null);
  const [flagged, setFlagged] = useState(0);
  const [recent, setRecent] = useState<RecentMove[]>([]);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    wms.locations().then((l) => { setLocations(l); setStatus({ ok: true, text: "Connected to Business Central" }); })
      .catch((e) => setStatus({ ok: false, text: errText(e) }));
    wms.recent().then(setRecent).catch(() => {});
  }, []);
  useEffect(() => {
    if (!loc) return;
    wms.picks(loc).then((p) => setCounts({ mine: p.mine.length, unassigned: p.unassigned.length }))
      .catch((e) => { if (errBody(e).code === "wms_not_installed") setStatus({ ok: false, text: errText(e) }); });
    wms.replen(loc).then((r) => setRepl({ total: r.tasks.length, urgent: r.tasks.filter((t) => t.priority === 1).length, rules: r.rules, feed: r.feed.on && r.feed.available })).catch(() => {});
    wmsCounts.sheets(loc).then((r) => setSheets({ mine: r.mine.length, open: r.unassigned.length })).catch(() => {});
    wmsCounts.flags().then((f) => setFlagged(new Set(f.filter((x) => x.locationCode === loc).map((x) => x.binCode)).size)).catch(() => {});
  }, [loc]);

  if (picking || !loc) return (
    <>
      <Header title="Choose your location" sub="Picks and bins follow the location you pick" back={loc ? () => setPicking(false) : undefined} />
      <div className="w-body">
        {!locations && !status && <p className="w-muted">Loading locations…</p>}
        {status && !status.ok && <Alert title="Can't reach Business Central">{status.text}</Alert>}
        <div className="w-list">
          {locations?.map((l) => (
            <button key={l.code} type="button" className={`w-row-btn ${l.code === loc ? "sel" : ""}`} onClick={() => { choose(l.code); setPicking(false); feedback.ok(); }}>
              <span className="w-row-main"><span className="w-strong w-mono">{l.code}</span><span className="w-muted">{l.name}</span></span>
              {l.code === loc && <I n="check" />}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const first = name.split(/\s+/)[0];
  return (
    <>
      <header className="w-head home">
        <div className="w-head-t"><div className="w-sub">{greeting()}, {first}</div><div className="w-title big">Warehouse</div></div>
        <button type="button" className="w-locbtn" aria-label={`Change location, now ${loc}`} onClick={() => setPicking(true)}><I n="home" s={18} />{loc}<I n="down" s={16} /></button>
      </header>
      <div className="w-body">
        <ScanField label="Look up a bin, item or pick" onScan={(c) => nav(`/wms/lookup?code=${encodeURIComponent(c)}`)} />
        <Link to="/wms/lookup" className="w-tile">
          <span className="w-tile-i"><I n="search" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Look up</span><span className="w-muted">A bin or an item: what's there and what moved</span></span>
          <I n="chev" />
        </Link>
        <Link to="/wms/move" className="w-tile">
          <span className="w-tile-i"><I n="move" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Move stock</span><span className="w-muted">Bin to bin, one item or many</span></span>
          <I n="chev" />
        </Link>
        <Link to="/wms/picks" className="w-tile">
          <span className="w-tile-i"><I n="pick" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Pick</span><span className="w-muted">{counts ? `${counts.mine} assigned to you · ${counts.unassigned} unassigned` : "Warehouse and inventory picks"}</span></span>
          {counts && counts.mine > 0 ? <span className="w-count">{counts.mine}</span> : <I n="chev" />}
        </Link>
        <Link to="/wms/replen" className="w-tile">
          <span className="w-tile-i"><I n="refill" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Replenish</span><span className="w-muted">{!repl ? "Pick faces and production lines" : repl.total ? `${repl.total} bin${repl.total === 1 ? "" : "s"} to fill${repl.urgent ? ` · ${repl.urgent} urgent` : ""}` : repl.rules || repl.feed ? "All bins stocked" : "No min and max levels set yet"}</span></span>
          {repl && repl.urgent > 0 ? <span className="w-count bad">{repl.urgent}</span> : <I n="chev" />}
        </Link>
        <Link to="/wms/count" className="w-tile">
          <span className="w-tile-i"><I n="count" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Count</span><span className="w-muted">{!sheets ? "Count sheets" : sheets.mine || sheets.open ? `${sheets.mine} yours · ${sheets.open} open to anyone` : "No sheets to count"}</span></span>
          {sheets && sheets.mine > 0 ? <span className="w-count">{sheets.mine}</span> : <I n="chev" />}
        </Link>
        <Link to="/wms/quick" className="w-tile">
          <span className="w-tile-i"><I n="quick" s={28} /></span>
          <span className="w-tile-t"><span className="w-tile-n">Quick count</span><span className="w-muted">{flagged ? `${flagged} bin${flagged === 1 ? "" : "s"} flagged to check` : "A bin or an item, right now"}</span></span>
          {flagged > 0 ? <span className="w-count bad">{flagged}</span> : <I n="chev" />}
        </Link>
        {recent.length > 0 && (
          <div className="w-section">
            <div className="w-label">Your last moves</div>
            <div className="w-card w-rows">
              {recent.slice(0, 3).map((m) => (
                <div key={m.id} className="w-line">
                  <span className="w-row-main"><span className="w-strong">{m.itemNo} · {fmt(m.qty)} {m.uom ?? ""}{m.status === "undone" ? " (undone)" : m.status === "failed" ? " (failed)" : ""}</span>
                    <span className="w-mono w-muted">{m.fromBin} → {m.toBin}</span></span>
                  <span className="w-muted">{new Date(m.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {status && <div className={`w-status ${status.ok ? "ok" : "bad"}`}><span className="w-dot" />{status.text}</div>}
        <Link to="/" className="w-small">Open courageMFG</Link>
      </div>
    </>
  );
}

/** "Today 2:37 PM", "Yesterday 4:12 PM", "Oct 29, 1:05 PM". */
function when(iso: string) {
  const d = new Date(iso), now = new Date();
  const t = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400_000);
  return diff === 0 ? `Today ${t}` : diff === 1 ? `Yesterday ${t}` : `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${t}`;
}
const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${fmt(Math.abs(n))}`;

/**
 * Look up a bin or an item: type to find one (bins by code, items by number or name) or scan it. A bin shows what's
 * in it, an item shows every bin it's in, and both show their recent movements from Business Central: moves, picks,
 * receipts, shipments and adjustments, newest first, with who did each one.
 */
function Lookup({ loc }: { loc: string }) {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const code = sp.get("code") ?? "";
  const [r, setR] = useState<ScanResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [found, setFound] = useState<Found | null>(null);
  const [mv, setMv] = useState<Movements | null>(null);
  const [mvErr, setMvErr] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    if (!code) { setR(null); return; }
    setR(null); setErr(null); setMv(null); setMvErr(null); setMore(false);
    wms.scan(loc, code).then((x) => {
      if (x.kind === "pick") { nav(`/wms/picks/${x.type}/${encodeURIComponent(x.no)}`, { replace: true }); return; }
      if (x.kind === "unknown") feedback.bad(); else feedback.ok();
      setR(x);
    }).catch((e) => { feedback.bad(); setErr(errText(e)); });
  }, [code, loc]); // eslint-disable-line react-hooks/exhaustive-deps
  // Recent movements, once the bin or item is known.
  const by = r?.kind === "bin" ? { bin: r.bin.code } : r?.kind === "item" ? { item: r.item.itemNo } : null;
  useEffect(() => {
    if (!by) return;
    setMvErr(null);
    wms.movements(loc, by, more ? 100 : 25).then(setMv).catch((e) => setMvErr(errText(e)));
  }, [r, more]); // eslint-disable-line react-hooks/exhaustive-deps
  // Suggestions as you type.
  useEffect(() => {
    const t = typed.trim();
    if (t.length < 2) { setFound(null); return; }
    const h = setTimeout(() => { wms.find(loc, t).then(setFound).catch(() => setFound(null)); }, 250);
    return () => clearTimeout(h);
  }, [typed, loc]);
  const open = (c: string) => { setTyped(""); setFound(null); setSp({ code: c }); };
  const total = (rows: { qty: number }[]) => fmt(rows.reduce((a, x) => a + x.qty, 0));
  const suggesting = typed.trim().length >= 2;
  return (
    <>
      <Header title="Look up" sub={r?.kind === "bin" ? `Bin ${r.bin.code}` : r?.kind === "item" ? r.item.itemNo : undefined} back="/wms" right={<LocChip code={loc} />} />
      <div className="w-body">
        <ScanField key={code} label="Find a bin or an item" placeholder="Scan or type" onScan={open} onType={setTyped} autoFocus={!code} />
        {suggesting && (
          <div className="w-card w-rows" role="listbox" aria-label="Matches">
            {!found && <div className="w-line w-muted">Looking…</div>}
            {found && !found.bins.length && !found.items.length && <div className="w-line w-muted">Nothing matches {typed.trim()}. Press Enter to look it up as a barcode.</div>}
            {found?.bins.map((b) => (
              <button key={`b${b.code}`} type="button" role="option" aria-selected={false} className="w-line link" onClick={() => open(b.code)}>
                <span className="w-row-main"><span className="w-strong w-mono">{b.code}</span><span className="w-muted">{["Bin", b.zone && `zone ${b.zone}`, b.blocked && `blocked: ${b.blocked.toLowerCase()}`].filter(Boolean).join(" · ")}</span></span>
                <I n="chev" />
              </button>))}
            {found?.items.map((i) => (
              <button key={`i${i.itemNo}`} type="button" role="option" aria-selected={false} className="w-line link" onClick={() => open(i.itemNo)}>
                <span className="w-row-main"><span className="w-strong">{i.itemNo}</span><span className="w-muted">{i.description || "Item"}</span></span>
                <I n="chev" />
              </button>))}
          </div>
        )}
        {!suggesting && <>
          {err && <Alert title="Couldn't look that up">{err}</Alert>}
          {!r && !err && code && <p className="w-muted">Looking up {code}…</p>}
          {!code && <p className="w-muted">Scan a bin label or an item's barcode, or start typing: bins by code, items by number or name.</p>}
          {r?.kind === "unknown" && <Alert tone="warn" title={`Nothing at ${loc} matches ${r.code}`}>It isn't a bin here, a pick, an item number or a barcode on an item.</Alert>}
          {r?.kind === "bin" && (
            <>
              <div className="w-hero"><span className="w-hero-l">Bin</span><span className="w-hero-c">{r.bin.code}</span><span className="w-hero-s">{[r.bin.zone && `Zone ${r.bin.zone}`, r.bin.description, r.bin.blockMovement && `Blocked: ${r.bin.blockMovement.toLowerCase()}`].filter(Boolean).join(" · ") || " "}</span></div>
              <div className="w-label">{r.contents.length ? `${r.contents.length} line${r.contents.length > 1 ? "s" : ""} in this bin` : "This bin is empty"}</div>
              {r.contents.length > 0 && <div className="w-card w-rows">
                {r.contents.map((x, i) => (
                  <button key={i} type="button" className="w-line link" onClick={() => open(x.itemNo)}>
                    <span className="w-row-main"><span className="w-strong">{x.itemNo}</span><span className="w-muted">{[x.description, x.lotNo && `Lot ${x.lotNo}`, x.serialNo && `SN ${x.serialNo}`].filter(Boolean).join(" · ")}</span></span>
                    <span className="w-strong">{fmt(x.qty)} {x.uom}</span>
                  </button>
                ))}
              </div>}
              <div className="w-two">
                {r.contents.length > 0 && <Link to={`/wms/move?from=${encodeURIComponent(r.bin.code)}`} className="w-btn primary">Move stock</Link>}
                <Link to={`/wms/quick?code=${encodeURIComponent(r.bin.code)}`} className="w-btn">Quick count</Link>
              </div>
            </>
          )}
          {r?.kind === "item" && (
            <>
              <div className="w-card w-item">
                <span className="w-item-i"><I n="box" s={28} w={1.8} /></span>
                <span className="w-row-main"><span className="w-strong big">{r.item.itemNo}</span><span className="w-muted">{r.item.description}</span></span>
              </div>
              <div className="w-label">{r.bins.length ? `${total(r.bins)} ${r.item.uom} in ${new Set(r.bins.map((b) => b.binCode)).size} bin${new Set(r.bins.map((b) => b.binCode)).size === 1 ? "" : "s"} at ${loc}` : `None in any bin at ${loc}`}</div>
              {r.bins.length > 0 && <div className="w-card w-rows">
                {r.bins.map((x, i) => (
                  <button key={i} type="button" className="w-line link" onClick={() => open(x.binCode)}>
                    <span className="w-row-main"><span className="w-strong w-mono">{x.binCode}</span>{(x.lotNo || x.serialNo) && <span className="w-muted">{x.lotNo ? `Lot ${x.lotNo}` : `SN ${x.serialNo}`}</span>}</span>
                    <span className="w-strong">{fmt(x.qty)} {x.uom}</span>
                  </button>
                ))}
              </div>}
              <Link to={`/wms/quick?code=${encodeURIComponent(r.item.itemNo)}`} className="w-btn">Quick count {r.item.itemNo}</Link>
            </>
          )}
          {by && (
            <div className="w-section">
              <div className="w-label">Recent movements</div>
              {mvErr && <Alert tone="warn" title="Couldn't load the movements">{mvErr}</Alert>}
              {!mv && !mvErr && <p className="w-muted">Loading movements…</p>}
              {mv?.note && <p className="w-small w-muted">{mv.note}</p>}
              {mv && !mv.entries.length && <div className="w-card w-empty">No movements {mv.source === "bc" ? "in Business Central" : "made in courageMFG"} yet.</div>}
              {mv && mv.entries.length > 0 && <div className="w-card w-rows">
                {mv.entries.map((e) => (
                  <div key={e.id} className="w-line w-mv">
                    <span className="w-row-main">
                      <span className="w-strong">{e.kind} · {r?.kind === "bin" ? e.itemNo : <span className="w-mono">{e.binCode || "no bin"}</span>}</span>
                      {(e.lotNo || e.serialNo || e.ref || e.doc) && <span className="w-muted">{[e.lotNo ? `Lot ${e.lotNo}` : e.serialNo ? `SN ${e.serialNo}` : null, e.ref, e.doc].filter(Boolean).join(" · ")}</span>}
                      <span className="w-small w-muted">{[e.by, when(e.at)].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className={`w-strong w-mv-q ${e.qty < 0 ? "out" : "in"}`}>{signed(e.qty)}</span>
                  </div>
                ))}
              </div>}
              {mv && mv.entries.length >= 25 && !more && <button type="button" className="w-link" onClick={() => setMore(true)}>Show more</button>}
            </div>
          )}
        </>}
      </div>
    </>
  );
}
